<template>
    <div class="position-relative">
        <ai-profile-search v-model="profile" @search="updateProfileSearch" :class="(mapList && uiStore.isMobile) ? null : 'content-container my-4 rounded rounded-5'" ref="aiProfileSearchBar" />
        
        <properties-search-bar v-model="searchObject" :quotes="quotes" :quotesNightly="quotesNightly" @search="search" @ready="searchBarReady"  @goToMap="goToMap" @goToGrid="goToGrid" ref="searchBarElm"/>

        <template v-if="mapList">

            <properties-map-list ref="mapElm"  :error="error" :searching="searching" :emptyResults="emptyResults" :properties="properties" :checkIn="checkIn" :checkOut="checkOut" :adults="adults" :children="children" :quotePerNight="quotePerNight" :searchBarReady="isSearchReady" :searchBar="$refs.searchBarElm" :refreshMap="refreshMap" :resultsCount="resultsCount" :quotes="quotes" @mapUpdated="mapUpdate" @changePage="changePage" @goToGrid="goToGrid" @setDisplayQuotePerStay="setDisplayQuotePerStay" @setDisplayQuotePerNight="setDisplayQuotePerNight" />

        </template>

        <template v-else>

            <div class="content-container mt-5">

                <container>
                    <row>
                        <column :col="20" :md="14" class="d-flex align-items-baseline">
                            <h1>Book a Property</h1>
                            <spinner v-if="!emptyResults && (searching || !properties)" class="ms-2" size="sm" />
                            <h2 v-else-if="resultsCount" class="ms-2 text-primary">({{ resultsCount }})</h2>
                        </column>
                        <column :col="4" :md="10" class="d-flex align-items-center justify-content-end">

                            <button-group v-if="!uiStore.isMobile && quotes" class="mb-3 me-1">
                                <ui-button :underline="true" :color="quotePerNight ? 'light' : 'primary'" @click="setDisplayQuotePerStay">
                                    $USD / Stay
                                </ui-button>
                                <ui-button :underline="true" :color="quotePerNight ? 'primary' : 'light'" @click="setDisplayQuotePerNight">
                                    $USD / Night
                                </ui-button>

                            </button-group>
                            
                            <ui-button class="mb-3 border-0 hover-bg-info d-flex align-items-center" @click="goToMap" color="none" :size="uiStore.isMobile ? 'sm' : 'md'">
                                <map-icon :size="20"/>
                            </ui-button>
                        </column>
                    </row>
                </container>

                <button-group v-if="uiStore.isMobile && quotes && !emptyResults" size="sm" class="my-3 w-100">
                    <!-- outline when not selected -->
                    <ui-button :outline="displayQuoteByType == 'night'" @click="setDisplayQuotePerStay">
                        $USD / Stay
                    </ui-button>
                    <ui-button :outline="displayQuoteByType != 'night'" @click="setDisplayQuotePerNight">
                        $USD / Night
                    </ui-button>

                </button-group>
            </div>

            <div v-if="error || (searching && properties)" class="py-3 py-md-4 py-lg-5 content-container">
                <divider height="sm" />

                <alert v-if="error">
                    {{ error }}
                    <ui-button class="ms-3" @click="requestFresh">
                        Try Again
                    </ui-button>
                </alert>

                <div v-if="(searching && properties)" class="text-center my-4">
                    <spinner/>
                    <p class="small text-muted">Loading Results</p>
                </div>
            </div>

            <template v-if="properties">

                <div v-if="hasSearch" class="py-3 py-md-4 py-lg-5 content-container">

                    <properties-list :properties="properties" :checkIn="checkIn" :checkOut="checkOut" :adults="adults" :children="children" :quotePerNight="quotePerNight"/>
                    
                </div>
                <properties-featured-list v-else :properties="properties" :checkIn="checkIn" :checkOut="checkOut" :adults="adults" :children="children" :quotePerNight="quotePerNight" :markets="markets"/>

                <pagination :collection="properties" @change="changePage" class="mt-5" />
                <divider :height="displayProfile ? 'sm' : 'md'" />

            </template>
            <div v-else-if="emptyResults" class="py-3 py-md-4 py-lg-5 content-container">
                <divider v-if="!uiStore.isMobile" height="sm" />
                <properties-empty-results :checkIn="checkIn" :checkOut="checkOut" :adults="adults" :children="children"/>
            </div>
            <div v-else-if="!error" class="py-3 py-md-4 py-lg-5 content-container">
                <divider height="sm" />
                <properties-loading/>
            </div>
        </template>

        <div v-if="displayProfile" class="content-container">
            <hr class="mb-4" />
            <ai-profile-gallery-plus :profile="displayProfile" />
            <divider />
        </div>
        
        <ai-profile-modal v-if="showAiProfileModal" :id="aiProfileId" :hash="aiProfileHash" @close="showAiProfileModal = false" @profile="setDisplayAiProfile" />
    </div>
</template>
<script type="text/javascript">
    import PropertiesList from '@/components/Content/Public/Properties/Lists/List.vue';
    import PropertiesMapList from '@/components/Content/Public/Properties/Lists/Map.vue';
    import PropertiesEmptyResults from '@/components/Content/Public/Properties/Lists/Empty.vue';
    import PropertiesFeaturedList from '@/components/Content/Public/Properties/Lists/Featured.vue';
    import PropertiesLoading from '@/components/Content/Public/Properties/Lists/Loading.vue';
    import PropertiesSearchBar from '@/components/Content/Public/Properties/Search/Bar.vue';
    import AiProfileSearch from '@/components/Content/Public/Properties/Search/AiProfile.vue';
    import AiProfileModal from '@/components/Content/Public/AI/ViewProfileModal.vue';
    import AiProfileGalleryPlus from '@/components/Content/Public/AI/GalleryPlus.vue';
    import MapIcon from '@/assets/icons/map.svg.vue';
    import CommonComponents from '@/components/Common.vue';

    import Range from '@/classes/Ranges/Range.js';
    import DateRange from '@/classes/Ranges/Date.js';
    import DateObject from '@/classes/DateObject.js';
    import ApiConfig from '@/config/api.json';

    import { UseApiStore } from '@/store/Api.js';
    import { UseUIStore } from '@/store/UI.js';

    export default {
        components: {
            PropertiesSearchBar,
            PropertiesList,
            PropertiesMapList,
            PropertiesEmptyResults,
            PropertiesFeaturedList,
            PropertiesLoading,
            AiProfileSearch,
            AiProfileGalleryPlus,
            MapIcon,
            AiProfileModal,
            ...CommonComponents,
        },

        setup() {
            return {
                apiStore: UseApiStore(),
                uiStore: UseUIStore(),
            };
        },

        data() {
            return {
                disposed: false,

                error: null,
                requestsTimer: null,
                emptyResults: null,

                searchObject: null,
                lastRequestedUrl: null,

                searching: false,
                firstLoad: true, // used to set the dates from affilaites
                isSearchReady: false,
                refreshMap: 0,

                displayQuoteByType: null,

                goingToGrid: false, // prevnt the query parameter from filling with the map data when switching back

                profile: null,

                displayProfile: null,
                showAiProfileModal: false,
                aiProfileId: null,
                aiProfileHash: null,
            };
        },

        created() {

            this.$watch(
                () => this.$route.params,
                this.setParamAndRequest,
                { immediate: true }
            );

            if (
                (this.$route.params.showProfile) &&
                (this.$route.params.profile)
            ) {
                let arr = this.$route.params.profile.split('-');
                if (arr.length == 2) {
                    this.aiProfileId = arr[0];
                    this.aiProfileHash = arr[1];
                    this.showAiProfileModal = true;
                }
            }
        },

        watch: {
            quotes() {
                this.refreshMap++;
            },
        },

        beforeRouteLeave(to, from) {
            if (
                (to.meta) &&
                (to.meta.multiRoute) &&
                (from.meta) &&
                (from.meta.multiRoute) &&
                (to.meta.multiRoute == from.meta.multiRoute)
            ) {
                return;
            }
            
            this.disposed = true;
            clearTimeout(this.requestsTimer);
        },

        computed: {

            requestUrl() {
                let params = this.urlQuery(this.searchObject);
                if (
                    (this.$route.params) &&
                    (this.$route.params.mapOrAffiliate) &&
                    (this.$route.params.mapOrAffiliate.toString().toLowerCase() != 'map')
                ) {
                    if (!params) {
                        params = {};
                    }
                    params.affIdx = this.$route.params.mapOrAffiliate;
                }
                
                return this.requestUrlFromData(params);
            },

            propertiesData() {
                let response = this.apiStore.requests[this.requestUrl];
                
                if (!response) {
                    response = this.apiStore.requests[this.lastRequestedUrl];
                }

                if (!response) {
                    return null;
                }

                if ((response.valid) && (response.results) && (!response.results.error)) {

                    if (
                        (!this.disposed) &&
                        (response.setMetaData) &&
                        (response.setMetaData instanceof Function)
                    ) {
                        response.setMetaData();
                    }
                    
                    if (
                        (!response.results.properties) ||
                        (response.results.properties.length == 0)
                    ) {
                        this.setEmptyResults(true);
                    }
                    this.setSearchDatesFromResults();
                    return response.results;
                }
                else {
                    let error = 'Could not complete request';
                    if ((response.results) && (response.results.error)) {
                        if (response.results.error.message) error = response.results.error.message;
                        else error = response.results.error;
                    }
                    this.setError(error);
                }
                
                return null;
            },

            properties() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.properties) &&
                    (this.propertiesData.properties.length)
                ) {
                    return this.propertiesData.properties;
                }
                return null;
            },

            markets() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.markets) &&
                    (this.propertiesData.markets.length)
                ) {
                    return this.propertiesData.markets;
                }
                return null;
            },

            resultsCount() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.resultsCount != null)
                ) {
                    return this.propertiesData.resultsCount;
                }
                return null;
            },

            quotes() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.quotes)
                ) {
                    let quotes = this.propertiesData.quotes;
                    // remove key "a" that is used to force the output
                    delete quotes.a;
                    return quotes;
                }
                return null;
            },

            quotesNightly() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.quotesNightly)
                ) {
                    return this.propertiesData.quotesNightly;
                }
                return null;
            },
            
            checkIn() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.checkIn)
                ) {
                    return this.propertiesData.checkIn;
                }
                return null;
            },

            checkOut() {
                if (
                    (this.propertiesData) &&
                    (this.propertiesData.checkOut)
                ) {
                    return this.propertiesData.checkOut;
                }
                return null;
            },

            adults() {
                if (this.searchObject) {
                    return this.searchObject.adults;
                }
                return null;
            },
            
            children() {
                if (this.searchObject) {
                    return this.searchObject.children;
                }
                return null;
            },

            quotePerNight() {
                if (this.displayQuoteByType == 'night') {
                    return true;
                }
                return false;
            },

            hasSearch() {
                if (this.searchObject) {
                    return (Object.keys(this.searchObject).length > 0);
                }
                return false;
            },

            mapList() {
                if (
                    (this.$route.params) &&
                    (this.$route.params.mapOrAffiliate) &&
                    (this.$route.params.mapOrAffiliate.toString().toLowerCase() == 'map')
                ) {
                    return true;
                }

                return false;
            },
        },

        methods: {

            requestUrlFromData(params) {
                let re = 'properties/list';
                if ((params) && (Object.keys(params).length > 0)) {
                    re += '?'+new URLSearchParams(params).toString();
                }
                return re;
            },

            setParamAndRequest() {
                this.setSearchFromParam();

                if ((this.hasSearch) && (!this.apiStore.requests[this.requestUrl])) {
                    this.searching = true;
                }

                this.requestData();
            },

            setSearchDatesFromResults() {
                if (!this.firstLoad) {
                    return;
                }
                this.firstLoad = false;

                requestAnimationFrame(function() {

                    if (
                        (!this.searchObject.dateRange) &&
                        (this.checkIn) &&
                        (this.checkOut)
                    ) {
                        let range = new DateRange(this.checkIn.copy(), this.checkOut.copy());
                        range.orient();
                        this.searchObject.dateRange = range;
                    }

                }.bind(this));
            },

            setSearchFromParam() {
                let searchObj = {};
                
                if (this.$route.params) {

                    if ((this.$route.params.cities) && (this.$route.params.cities.length)) {
                        searchObj.cities = this.$route.params.cities;
                    }
                    if ((this.$route.params.markets) && (this.$route.params.markets.length)) {
                        searchObj.markets = this.$route.params.markets;
                    }

                    if (
                        (this.$route.params.minBedrooms) &&
                        (this.$route.params.maxBedrooms)
                    ) {
                        let mn = parseFloat(this.$route.params.minBedrooms);
                        let mx = parseFloat(this.$route.params.maxBedrooms);
                        if ((!isNaN(mn)) && (!isNaN(mx))) {
                            searchObj.bedroomsRange = new Range(mn, mx);
                            searchObj.bedroomsRange.orient();
                        }
                    }

                    if (
                        (this.$route.params.minBathrooms) &&
                        (this.$route.params.maxBathrooms)
                    ) {
                        let mn = parseFloat(this.$route.params.minBathrooms);
                        let mx = parseFloat(this.$route.params.maxBathrooms);
                        if ((!isNaN(mn)) && (!isNaN(mx))) {
                            searchObj.bathroomsRange = new Range(mn, mx);
                            searchObj.bathroomsRange.orient();
                        }
                    }

                    if (
                        (this.$route.params.minOccupancy) &&
                        (this.$route.params.maxOccupancy)
                    ) {
                        let mn = parseFloat(this.$route.params.minOccupancy);
                        let mx = parseFloat(this.$route.params.maxOccupancy);
                        if ((!isNaN(mn)) && (!isNaN(mx))) {
                            searchObj.occupancyRange = new Range(mn, mx);
                            searchObj.occupancyRange.orient();
                        }
                    }

                    if (this.$route.params.adults) {
                        let adults = parseFloat(this.$route.params.adults);
                        if (!isNaN(adults)) {
                            searchObj.adults = adults;
                        }
                    }
                    if (this.$route.params.children) {
                        let children = parseFloat(this.$route.params.children);
                        if (!isNaN(children)) {
                            searchObj.children = children;
                        }
                    }

                    if (this.$route.params.pets) {
                        searchObj.pets = true;
                    }
                    if (this.$route.params.marquisLevel) {
                        searchObj.marquisLevel = true;
                    }

                    if ((this.$route.params.features) && (this.$route.params.features.length)) {
                        searchObj.features = this.$route.params.features;
                    }
                    
                    if (
                        (this.$route.params.start) &&
                        (this.$route.params.end)
                    ) {

                        let start = new DateObject(this.$route.params.start).startOfDay();
                        let end = new DateObject(this.$route.params.end).startOfDay();
                        if ((start.valid) && (end.valid)) {
                            searchObj.dateRange = new DateRange(start, end);
                            searchObj.dateRange.orient();

                            if (this.$route.params.pricingType) {
                                searchObj.priceRangeType = this.$route.params.pricingType;
                            }
                        }
                    }

                    if (this.$route.params.page) {
                        let page = parseFloat(this.$route.params.page);
                        if ((!isNaN(page)) && (page > 1)) {
                            searchObj.page = page;
                        }
                    }

                    if ((this.$route.params.minPrice) && (this.$route.params.maxPrice)) {
                        let mn = parseFloat(this.$route.params.minPrice);
                        let mx = parseFloat(this.$route.params.maxPrice);
                        if ((!isNaN(mn)) && (!isNaN(mx))) {
                            searchObj.priceRange = new Range(mn, mx);
                            searchObj.priceRange.orient();

                            if (
                                (this.$route.params.pricingType) &&
                                (
                                    (this.$route.params.pricingType == 'night') ||
                                    (this.$route.params.pricingType == 'stay')
                                )
                            ) {
                                searchObj.priceRangeType = this.$route.params.pricingType;
                            }
                        }
                    }

                    if (this.$route.params.favorites) {
                        searchObj.favorites = true;
                    }

                    if ((this.$route.params.sort) && (['priceDesc', 'priceAsc', 'ratings'].indexOf(this.$route.params.sort) != -1)) {
                        searchObj.sort = this.$route.params.sort;
                    }

                    if (this.$route.params.profile) {
                        searchObj.profile = this.$route.params.profile;
                        this.profile = this.$route.params.profile;
                    }
                    else {
                        this.profile = null;
                    }
                }

                this.searchObject = searchObj;
            },

            urlQuery(obj) {
                if (!obj) return;

                let query = {};

                if ((obj.cities) && (obj.cities.length)) {
                    // we'll add these as array keys so the
                    // server can process them as arrays
                    for (let i=0; i<obj.cities.length; i++) {
                        query['cities['+i+']'] = obj.cities[i];
                    }
                }

                if ((obj.markets) && (obj.markets.length)) {
                    // we'll add these as array keys so the
                    // server can process them as arrays
                    for (let i=0; i<obj.markets.length; i++) {
                        query['markets['+i+']'] = obj.markets[i];
                    }
                }

                if (obj.bedroomsRange) {
                    query.minBedrooms = obj.bedroomsRange.start;
                    query.maxBedrooms = obj.bedroomsRange.end;
                }

                if (obj.bathroomsRange) {
                    query.minBathrooms = obj.bathroomsRange.start;
                    query.maxBathrooms = obj.bathroomsRange.end;
                }

                if (obj.occupancyRange) {
                    query.minOccupancy = obj.occupancyRange.start;
                    query.maxOccupancy = obj.occupancyRange.end;
                }

                if (obj.adults) {
                    query.adults = obj.adults;
                }
                if (obj.children) {
                    query.children = obj.children;
                }
                if (obj.pets) {
                    query.pets = 1;
                }
                if (obj.marquisLevel) {
                    query.marquisLevel = 1;
                }
                if ((obj.features) && (obj.features.length)) {
                    // we'll add these as array keys so the
                    // server can process them as arrays
                    for (let i=0; i<obj.features.length; i++) {
                        query['features['+i+']'] = obj.features[i];
                    }
                }
                if (obj.dateRange) {
                    query.start = obj.dateRange.start.format('Y-m-d');
                    query.end = obj.dateRange.end.format('Y-m-d');
                }
                if (obj.priceRange) {
                    query.minPrice = obj.priceRange.start;
                    query.maxPrice = obj.priceRange.end;

                    if (obj.priceRangeType) {
                        query.pricingType = obj.priceRangeType;
                    }
                }

                if (obj.favorites) {
                    query.favorites = 1;
                }

                if (obj.sort) {
                    query.sort = obj.sort;
                }

                if ((obj.page) && (obj.page > 1)) {
                    query.page = obj.page;
                }

                // adding the map params
                if ((!this.goingToGrid) && (this.mapList) && (this.$refs.mapElm)) {
                    let mapParams = this.$refs.mapElm.mapParams();
                    if (mapParams) {
                        query = {...query, ...mapParams};
                    }
                }

                if (obj.profile) {
                    query.profile = obj.profile;
                }

                if (Object.keys(query).length) {
                    return query;
                }
                
                return null;
            },

            requestFresh() {
                this.requestData(true);
            },

            async requestData(fresh) {
                if (this.disposed) return;
                
                this.setError(null);
                this.setEmptyResults(null);
                
                clearTimeout(this.requestsTimer);

                let payload = {
                    url: this.requestUrl,
                };
                if (fresh) {
                    payload.fresh = true;
                }

                await this.apiStore.get(payload);

                this.lastRequestedUrl = payload.url;

                requestAnimationFrame(function() {
                    this.searching = false;
                }.bind(this));

                this.requestsTimer = setTimeout(this.requestData, ApiConfig.dashboardCache * 1000);
            },

            setError(error) {
                this.error = error;
            },

            setEmptyResults(val) {
                this.emptyResults = val;
            },

            goToMap() {
                this.goToMapOrGrid(true);
            },

            goToGrid() {
                this.goingToGrid = true;
                this.goToMapOrGrid(false);
                this.goingToGrid = false;
            },

            goToMapOrGrid(map) {
                let url = '/book-a-property';
                if (map) {
                    url += '/map';
                }

                let param = this.urlQuery(this.searchObject);
                if ((param) && (Object.keys(param).length > 0)) {
                    url += '?'+new URLSearchParams(param).toString();
                }

                if ((this.$route) && (this.$route.fullPath == url)) {
                    return;
                }
                
                this.$router.replace(url);
            },

            search(data) {
                let param = this.urlQuery(data);
                this.updateSearchQuery(param, true, false);
            },

            mapUpdate() {
                if (this.searchObject) {
                    delete this.searchObject.page;
                }

                let param = this.urlQuery(this.searchObject);
                this.updateSearchQuery(param, true, true);
            },

            updateProfileSearch(profile) {
                if (!this.searchObject) {
                    this.searchObject = {};
                }
                else {
                    delete this.searchObject.page;
                }
                this.searchObject.profile = profile;
                this.profile = profile;

                let param = this.urlQuery(this.searchObject);
                this.updateSearchQuery(param, true, false);
            },

            changePage(page) {

                if (!this.searchObject) {
                    this.searchObject = {};
                }
                if ((page) && (page > 1)) {
                    this.searchObject.page = page;
                }
                else {
                    delete this.searchObject.page;
                }

                let param = this.urlQuery(this.searchObject);
                this.updateSearchQuery(param, false, false);
                
            },

            // the param must be pre-processed by urlQuery
            updateSearchQuery(param, refreshMap, replace) {

                let url = '/book-a-property';
                if (this.mapList) {
                    url += '/map';
                }

                if ((param) && (Object.keys(param).length)) {
                    url += '?'+new URLSearchParams(param).toString();
                }

                if ((this.$route) && (this.$route.fullPath == url)) {
                    return;
                }

                let requestUrl = this.requestUrlFromData(param);
                if (!this.apiStore.requests[requestUrl]) {
                    this.searching = true;
                }
                
                if (replace) {
                    this.$router.replace(url);
                }
                else {
                    this.$router.push(url);
                }
                if (refreshMap) {
                    this.refreshMap++;
                }

            },

            searchBarReady() {
                this.isSearchReady = true;
            },

            setDisplayQuotePerStay() {
                this.displayQuoteByType = 'stay';
            },

            setDisplayQuotePerNight() {
                this.displayQuoteByType = 'night';
            },

            setDisplayAiProfile(profile) {
                this.displayProfile = profile;
                requestAnimationFrame(function() {
                    if (
                        (this.$refs.aiProfileSearchBar) &&
                        (this.$refs.aiProfileSearchBar.requestFresh) 
                    ) {
                        this.$refs.aiProfileSearchBar.requestFresh();
                    }
                }.bind(this));
            },
            
        }
    }
</script>